import type { EntityKind, GlobalDocumentHit, GlobalSearchResponse } from '@hoopoe/shared';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { ArrowRight, BookOpen, FileText, Lock, Search } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useColorMode } from '../../lib/colors';
import { DIRECTORY_TABS, markProps, typeLabelOf } from '../../lib/entities';
import { useSchema } from '../../lib/schema';
import { isSearchable, useGlobalSearch } from '../../lib/search';
import { DataText } from '../data/data-text';
import { EntityMark, STATUS_STROKE } from '../data/entity-mark';
import { useHelpPanel } from './help-panel';
import { useHelpSearch, useReportMiss } from './help-search';

const Ctx = createContext<{ open: () => void } | null>(null);
export const useCommandPalette = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useCommandPalette needs <CommandPaletteProvider>');
  return c;
};

type Entity = GlobalSearchResponse['entities'][number];

const groupClass = 'text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5';
const itemClass =
  'flex cursor-pointer items-start gap-2 rounded px-2 py-2 text-sm text-foreground data-[selected=true]:bg-muted aria-selected:bg-muted';

/**
 * ⌘K global search (PLAN §8, §12.6; task 23): actors, events, places, assets, sources and cases (names in any
 * script, aliases, codes; hybrid with meaning search where available), Library documents, and help articles.
 * Results are grouped by kind; each group links to the full list when there are more. Help results open in the side
 * panel so the user stays on their screen.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const index = useHelpSearch();
  const hits = useMemo(() => index.search(q, 5), [index, q]);
  useReportMiss(q, hits);
  const panel = useHelpPanel();
  const navigate = useNavigate();
  const server = useGlobalSearch(open ? q : '');
  const { data: schema } = useSchema();
  const { mode } = useColorMode();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);

  const typed = q.trim();
  const data = server.data;
  const byKind = useMemo(() => {
    const m = new Map<EntityKind, Entity[]>();
    for (const e of data?.entities ?? []) m.set(e.kind, [...(m.get(e.kind) ?? []), e]);
    return m;
  }, [data]);
  const documents = data?.documents ?? [];
  const anyServer = (data?.entities.length ?? 0) + documents.length > 0;
  const nothing = !!typed && !hits.length && !anyServer && !server.pending && !server.addingMeaning;

  const mark = (e: Entity) => {
    if (!schema) return <EntityMark plain kind={e.kind === 'actor' ? 'actor_org' : e.kind} status={e.reviewStatus} />;
    return <EntityMark plain {...markProps(schema, e, mode)} />;
  };

  return (
    <Ctx.Provider value={{ open: () => setOpen(true) }}>
      {children}
      <Dialog.Root
        open={open}
        onOpenChange={o => {
          setOpen(o);
          if (!o) setQ('');
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed top-[10vh] left-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 overflow-hidden rounded-lg border bg-surface-raised shadow-xl"
          >
            <Dialog.Title className="sr-only">Search</Dialog.Title>
            <Command shouldFilter={false} label="Search">
              <div className="flex items-center gap-2 border-b px-3">
                <Search className="size-4 text-muted-foreground" aria-hidden="true" />
                <Command.Input
                  value={q}
                  onValueChange={setQ}
                  placeholder="Search actors, sources, help…"
                  className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              <Command.List className="max-h-[60vh] overflow-auto p-1" aria-busy={server.pending || undefined}>
                {!typed && (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Type a name in any script, a code, words from a document, or a help topic.
                  </p>
                )}
                {nothing && (
                  <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No results for “<DataText>{typed}</DataText>”.
                  </Command.Empty>
                )}

                {DIRECTORY_TABS.filter(t => byKind.has(t.kind)).map(tab => (
                  <Command.Group key={tab.kind} heading={tab.label} className={groupClass}>
                    {(byKind.get(tab.kind) ?? []).map(e => (
                      <Command.Item
                        key={e.id}
                        value={`entity:${e.id}`}
                        onSelect={() => {
                          close();
                          navigate({ to: '/entity/$id', params: { id: e.id } });
                        }}
                        className={itemClass}
                      >
                        <span className="mt-0.5 flex">{mark(e)}</span>
                        <span className="min-w-0">
                          <span className="block truncate">
                            <DataText className="font-medium">{e.displayName}</DataText>
                            {e.matchedName && (
                              <span className="text-muted-foreground">
                                {' '}
                                (<DataText>{e.matchedName}</DataText>)
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {(schema && typeLabelOf(schema, e.typeId)) || tab.singular}
                            {' · '}
                            <span dir="ltr">{e.code}</span>
                            {e.reviewStatus !== 'verified' && ` · ${STATUS_STROKE[e.reviewStatus].label.split(':')[0]}`}
                            {e.sensitivity === 'confidential' && (
                              <>
                                {' · '}
                                <Lock className="inline size-3 align-[-2px]" aria-hidden="true" /> Confidential
                              </>
                            )}
                            {e.via === 'vector' && ' · similar name'}
                          </span>
                        </span>
                      </Command.Item>
                    ))}
                    {data?.more.includes(tab.kind) && (
                      <Command.Item
                        value={`more:${tab.kind}`}
                        onSelect={() => {
                          close();
                          navigate({ to: '/directory', search: { kind: tab.kind, q: typed } });
                        }}
                        className={itemClass}
                      >
                        <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span>
                          All {tab.label.toLowerCase()} matching “<DataText>{typed}</DataText>”
                        </span>
                      </Command.Item>
                    )}
                  </Command.Group>
                ))}

                {documents.length > 0 && (
                  <Command.Group heading="Library" className={groupClass}>
                    {documents.map(d => (
                      <DocumentItem
                        key={d.documentId}
                        d={d}
                        onSelect={() => {
                          close();
                          navigate({ to: '/library/$id', params: { id: d.documentId } });
                        }}
                      />
                    ))}
                    {data?.moreDocuments && (
                      <Command.Item
                        value="more:library"
                        onSelect={() => {
                          close();
                          navigate({ to: '/library', search: { q: typed } });
                        }}
                        className={itemClass}
                      >
                        <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span>
                          Search the Library for “<DataText>{typed}</DataText>”
                        </span>
                      </Command.Item>
                    )}
                  </Command.Group>
                )}

                {hits.length > 0 && (
                  <Command.Group heading="Help" className={groupClass}>
                    {hits.map(h => (
                      <Command.Item
                        key={h.id}
                        value={`help:${h.id}`}
                        onSelect={() => {
                          close();
                          panel.open(h.slug, h.section);
                        }}
                        className={itemClass}
                      >
                        <BookOpen className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span className="min-w-0">
                          <span className="font-medium">
                            {h.title}
                            {h.heading && <span className="text-muted-foreground"> › {h.heading}</span>}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">{h.snippet}</span>
                        </span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}
              </Command.List>
              <div className="flex items-center gap-3 border-t px-3 py-1.5 text-xs text-muted-foreground">
                <p role="status" className="min-w-0 flex-1">
                  {!isSearchable(q)
                    ? typed
                      ? 'Type one more character to search names and documents.'
                      : '↑ ↓ to move · Enter to open · Esc to close'
                    : server.pending
                      ? 'Searching…'
                      : server.error
                        ? 'Names and documents could not be searched. Try again.'
                        : server.addingMeaning
                          ? 'Looking for names in other scripts and similar passages…'
                          : data?.vectors && data.added.size
                            ? `Words, names in every script and meaning (${data.added.size} added by meaning).`
                            : data?.vectors
                              ? 'Words, names in every script and meaning.'
                              : 'Words and names in every script.'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    panel.open('global-search');
                  }}
                  className="shrink-0 underline underline-offset-2 hover:text-foreground"
                >
                  How search works
                </button>
              </div>
            </Command>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  );
}

function DocumentItem({ d, onSelect }: { d: GlobalDocumentHit; onSelect: () => void }) {
  return (
    <Command.Item value={`document:${d.documentId}`} onSelect={onSelect} className={itemClass}>
      <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block truncate">
          <DataText className="font-medium">{d.title || d.filename}</DataText>
          {d.title && d.title !== d.filename && (
            <span className="text-muted-foreground">
              {' · '}
              <DataText>{d.filename}</DataText>
            </span>
          )}
          {d.sensitivity === 'confidential' && (
            <span className="text-xs text-muted-foreground">
              {' '}
              <Lock className="inline size-3 align-[-2px]" aria-hidden="true" /> Confidential
            </span>
          )}
        </span>
        {d.passage ? (
          <span className="block truncate text-xs text-muted-foreground">
            {d.via === 'vector' ? 'Similar meaning · ' : ''}
            {d.passage.page ? `p. ${d.passage.page} · ` : ''}
            <DataText>{d.passage.snippet}</DataText>
          </span>
        ) : (
          <span className="block text-xs text-muted-foreground">File name matches</span>
        )}
      </span>
    </Command.Item>
  );
}
